import React from 'react'
import { Flex, Text, Button, Box, Icon, Spacer } from '@chakra-ui/react'
import { FaAngleDoubleLeft, FaChevronLeft, FaClipboard } from 'react-icons/fa'
import { LuExternalLink, LuMinus, LuX } from 'react-icons/lu'
import { navigate } from 'gatsby'

import { MobilePatientSelect } from './patient-select'
import DownloadMsgsModalButton from './more-options/download-msgs-modal-button'
import { useChat } from '../chat-context'
import SharedStack from '~components/shared/wrappers/shared-stack'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import ChatMoreOptions from './more-options'
import { PopOutChatButton } from '~components/chats/pop-out-chat-action'
import { useExportMessages } from '../export-messages-context'
import { getWritebackEnabled, getClientFullName } from '~utils/helpers'
import ChangeLogButton from '~components/shared/modals/change-log/change-log-button'
import ChatHeaderStatusSelect from './status-select'
import ChatAssignee from '~components/chats/chats-list/chat-preview/chat-assignee'
import ChatStatusSelect from '~components/chats/chats-list/chat-preview/chat-status-select'
import { FaAngleLeft, FaCloudArrowUp, FaUserGear } from 'react-icons/fa6'
import PatientTabs from './patient-tabs'
import { constructQueryString } from '~utils/construct-query-string'
import { UserRoutes } from '~config/routes'
import ExportSelectionMenu from './export-selection-menu'
interface ChatViewHeaderProps {
    onBack: () => void
    showClientInfo: boolean
    onOpen: () => void
    onOpenDrawer: () => void
    /**
     * Compact layout for embedded contexts like the Whiteboard chat popover.
     * Renders a denser 2-row header: title + window controls on top, action
     * icons on bottom. Avoids overlapping the popover close button and gives
     * the title room to truncate cleanly.
     */
    compact?: boolean
    /**
     * If provided in compact mode, renders a close (X) button in the header.
     * Used by the chat popover to close itself.
     */
    onClose?: () => void
    /**
     * If provided in compact mode, renders a minimize (-) button in the header.
     * Used by the chat popover to collapse to a title bar.
     */
    onMinimize?: () => void
    canShowMessagesForAllLocations?: boolean
    showMessagesForAllLocations: boolean
    setShowMessagesForAllLocations: React.Dispatch<
        React.SetStateAction<boolean>
    >
}

const ChatViewHeader: React.FC<ChatViewHeaderProps> = ({
    onBack,
    showClientInfo,
    onOpen,
    onOpenDrawer,
    compact = false,
    onClose,
    onMinimize,
    canShowMessagesForAllLocations,
    showMessagesForAllLocations,
    setShowMessagesForAllLocations,
}) => {
    const {
        isExporting,
        selectedMessages,
        canExport,
        cancelExport,
        handleCopy,
        handlePimsWriteback,
    } = useExportMessages()
    const { chat } = useChat()
    const writebackIsEnabled = getWritebackEnabled(chat.clinic.pms)

    function handleOpenInInbox() {
        navigate(UserRoutes.ChatConversation(chat.id))
    }

    if (compact && !isExporting) {
        return (
            <Box w="full" borderBottomWidth={1} borderBottomColor="gray.200">
                <Flex pt={2} pb={1} pl={8} pr={3} align="center" gap={2}>
                    <Box flex={1} minW={0} overflow="hidden">
                        <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            noOfLines={1}
                            cursor="pointer"
                            _hover={{ color: 'brand.600' }}
                            onClick={() =>
                                navigate(
                                    constructQueryString({
                                        newClient: chat.client.id,
                                    }),
                                )
                            }
                        >
                            {getClientFullName(chat.client) || 'No Client Name'}
                        </Text>
                    </Box>
                    <SharedStack row fit spacing={1}>
                        <IconButtonWithTooltip
                            icon={<Icon as={LuExternalLink} fontSize="sm" />}
                            label="Open in Inbox"
                            onClick={handleOpenInInbox}
                            size="xs"
                            rounded="full"
                            variant="ghost"
                            h="28px"
                            w="28px"
                        />
                        {onMinimize && (
                            <IconButtonWithTooltip
                                icon={<Icon as={LuMinus} fontSize="md" />}
                                label="Minimize"
                                onClick={onMinimize}
                                size="xs"
                                rounded="full"
                                variant="ghost"
                                h="28px"
                                w="28px"
                            />
                        )}
                        {onClose && (
                            <IconButtonWithTooltip
                                icon={<Icon as={LuX} fontSize="md" />}
                                label="Close"
                                onClick={onClose}
                                size="xs"
                                rounded="full"
                                variant="ghost"
                                h="28px"
                                w="28px"
                            />
                        )}
                    </SharedStack>
                </Flex>
                <Flex pb={2} px={3} align="center" gap={1.5}>
                    <ChatAssignee chat={chat} size="xs" />
                    <ChatStatusSelect chat={chat} size="xs" />
                    <ChatHeaderStatusSelect />
                    <ChangeLogButton entityId={chat.id} />
                    <ChatMoreOptions
                        chat={chat}
                        canShowMessagesForAllLocations={
                            canShowMessagesForAllLocations ?? false
                        }
                        showMessagesForAllLocations={
                            showMessagesForAllLocations
                        }
                        setShowMessagesForAllLocations={
                            setShowMessagesForAllLocations
                        }
                        showPopOut={false}
                    />
                    <IconButtonWithTooltip
                        icon={<Icon as={FaUserGear} fontSize="sm" />}
                        label="View Client Info"
                        onClick={onOpenDrawer}
                        size="xs"
                        rounded="full"
                        variant="outline"
                        h="28px"
                        w="28px"
                    />
                </Flex>
                <PatientTabs showClientInfo={showClientInfo} />
            </Box>
        )
    }

    return (
        <Box w="full" borderBottomWidth={1} borderBottomColor="gray.200">
            <Flex
                w="full"
                pt={4}
                pb={1}
                px={{ base: 4, lg: 6 }}
                justifyContent="space-between"
                alignItems={{
                    base: 'flex-start',
                    md: 'center',
                }}
                flexDir={{
                    base: isExporting ? 'column' : 'row',
                    lg: 'row',
                }}
            >
                <SharedStack fit row spacing={3}>
                    <IconButtonWithTooltip
                        icon={<Icon as={FaAngleLeft} fontSize="sm" />}
                        label="Go Back"
                        onClick={onBack}
                        size="xs"
                        rounded="full"
                        variant="outline"
                        h="28px"
                        w="28px"
                        display={{
                            base: isExporting ? 'none' : 'flex',
                            md: 'none',
                        }}
                    />
                    <Text
                        fontWeight="semibold"
                        fontSize="md"
                        display={{
                            base: 'none',
                            lg: 'block',
                        }}
                        tabIndex={0}
                        textDecoration="underline"
                        cursor="pointer"
                        _hover={{ color: 'brand.600' }}
                        onClick={() =>
                            navigate(
                                constructQueryString({
                                    newClient: chat.client.id,
                                }),
                            )
                        }
                    >
                        {getClientFullName(chat.client) || 'No Client Name'}
                    </Text>
                </SharedStack>
                <Spacer />
                <SharedStack row fit>
                    {isExporting ? (
                        <>
                            <ExportSelectionMenu />
                            <Button
                                size="xs"
                                height="28px"
                                px={3}
                                rounded="full"
                                variant="subtle"
                                bg="white"
                                _hover={{
                                    bg: 'gray.50',
                                }}
                                _active={{
                                    bg: 'gray.100',
                                }}
                                borderWidth={1}
                                borderColor="gray.200"
                                onClick={handleCopy}
                                isDisabled={!canExport}
                                leftIcon={<Icon as={FaClipboard} />}
                            >
                                Copy
                            </Button>
                            <DownloadMsgsModalButton
                                chat={chat}
                                messages={selectedMessages}
                                canExport={canExport}
                            />
                            {writebackIsEnabled && (
                                <Button
                                    size="xs"
                                    height="28px"
                                    px={3}
                                    rounded="full"
                                    colorScheme="brand"
                                    onClick={handlePimsWriteback}
                                    isDisabled={!canExport}
                                    leftIcon={<Icon as={FaCloudArrowUp} />}
                                >
                                    Save to PIMS
                                </Button>
                            )}
                            <Button
                                size="xs"
                                height="28px"
                                px={3}
                                rounded="full"
                                variant="subtle"
                                borderWidth={1}
                                borderColor="gray.200"
                                onClick={() => cancelExport()}
                            >
                                <Box
                                    as="span"
                                    display={{
                                        base: 'none',
                                        lg: 'inline',
                                    }}
                                >
                                    Cancel
                                </Box>
                                <Box
                                    as="span"
                                    display={{
                                        base: 'inline',
                                        lg: 'none',
                                    }}
                                >
                                    X
                                </Box>
                            </Button>
                        </>
                    ) : (
                        <>
                            <ChatAssignee chat={chat} size="xs" />
                            <ChatStatusSelect chat={chat} size="xs" />
                            <ChatHeaderStatusSelect />
                            <ChangeLogButton entityId={chat.id} />
                            <PopOutChatButton chatId={chat.id} />
                            <ChatMoreOptions
                                chat={chat}
                                canShowMessagesForAllLocations={
                                    canShowMessagesForAllLocations ?? false
                                }
                                showMessagesForAllLocations={
                                    showMessagesForAllLocations
                                }
                                setShowMessagesForAllLocations={
                                    setShowMessagesForAllLocations
                                }
                            />
                            {!showClientInfo && (
                                <IconButtonWithTooltip
                                    icon={
                                        <Icon
                                            as={FaAngleDoubleLeft}
                                            fontSize="sm"
                                        />
                                    }
                                    label="View Client Info"
                                    onClick={onOpen}
                                    size="xs"
                                    rounded="full"
                                    variant="outline"
                                    h="28px"
                                    w="28px"
                                />
                            )}
                            <IconButtonWithTooltip
                                icon={<Icon as={FaUserGear} fontSize="sm" />}
                                label="View Client Info"
                                onClick={onOpenDrawer}
                                size="xs"
                                rounded="full"
                                variant="outline"
                                h="28px"
                                w="28px"
                                display={{
                                    base: 'flex',
                                    lg: 'none',
                                }}
                            />
                        </>
                    )}
                </SharedStack>
            </Flex>
            <PatientTabs showClientInfo={showClientInfo} />
        </Box>
    )
}

export default ChatViewHeader
